<template>
  <div
    v-if="isOpen"
    :class="['fou-md-drawer-overlay', { 'fou-md-drawer-overlay--hidden': !showOverlay }]"
    @click="handleBackdropClick"
  >
    <!-- Drawer Container -->
    <div
      :class="[
        'fou-md-drawer',
        `fou-md-drawer--${position}`,
        { 'fou-md-drawer--no-transition': transition === 'none' }
      ]"
      :style="drawerStyles"
      @click.stop
    >
      <!-- Close Icon (not button) -->
      <MdIconClose v-if="showCloseButton" class="fou-md-drawer__close-icon" @click="closeDrawer" />

      <!-- Header Section -->
      <header
        v-if="$slots.header"
        :class="['fou-md-drawer__header', { 'fou-md-drawer__header--no-border': !bordered }]"
      >
        <slot name="header" />
      </header>

      <!-- Body Section (scrollable) -->
      <main :class="['fou-md-drawer__body', drawerBodyClass]">
        <slot name="body">
          <slot />
        </slot>
      </main>

      <!-- Footer Section -->
      <footer
        v-if="$slots.footer"
        :class="['fou-md-drawer__footer', { 'fou-md-drawer__footer--no-border': !bordered }]"
      >
        <slot name="footer" />
      </footer>
    </div>
  </div>
</template>

<script setup>
import { computed, watch, onBeforeUnmount } from 'vue';
import { MdIconClose } from '@antiqua-dls/vue-components';

// Define props
const props = defineProps({
  isOpen: {
    type: Boolean,
    default: false
  },
  position: {
    type: String,
    default: 'right',
    validator: value => ['left', 'right'].includes(value)
  },
  width: {
    type: String,
    default: '400px'
  },
  showOverlay: {
    type: Boolean,
    default: true
  },
  showCloseButton: {
    type: Boolean,
    default: true
  },
  enableBackDropClick: {
    type: Boolean,
    default: true
  },
  transition: {
    type: String,
    default: 'slide'
  },
  bordered: {
    type: Boolean,
    default: true
  },
  drawerBodyClass: {
    type: String,
    default: ''
  },
  bodyVerticalPadding: {
    type: String,
    default: 'var(--md-spacing-unit--md)'
  },
  bodyHorizontalPadding: {
    type: String,
    default: 'var(--md-spacing-unit--lg)'
  }
});

// Define emits
const emit = defineEmits(['close', 'open', 'update:isOpen']);

// Computed properties
const isPercentage = computed(() => {
  return props.width.includes('%');
});

const drawerStyles = computed(() => {
  return {
    width: props.width,
    maxWidth: isPercentage.value ? '95vw' : props.width,
    '--body-vertical-padding': props.bodyVerticalPadding,
    '--body-horizontal-padding': props.bodyHorizontalPadding
  };
});

// Methods
const closeDrawer = () => {
  emit('close');
  emit('update:isOpen', false);
};

const handleBackdropClick = () => {
  if (props.enableBackDropClick) {
    closeDrawer();
  }
};

// Watch for isOpen changes
watch(
  () => props.isOpen,
  newVal => {
    if (newVal) {
      emit('open');
      // Prevent body scroll when drawer is open
      document.body.style.overflow = 'hidden';
    } else {
      // Restore body scroll when drawer is closed
      document.body.style.overflow = '';
    }
  }
);

// Cleanup on component unmount
onBeforeUnmount(() => {
  // Cleanup: restore body scroll
  document.body.style.overflow = '';
});
</script>

<style lang="scss" scoped>
/* ===================
   OVERLAY STYLES
   =================== */
.fou-md-drawer-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1000;
  display: flex;

  // Hidden overlay (when showOverlay=false)
  &--hidden {
    background-color: transparent;
    pointer-events: none;
  }

  // Responsive overlay adjustments
  @media (max-width: 768px) {
    // Slightly darker overlay on mobile for better contrast
    background-color: rgba(0, 0, 0, 0.6);
  }

  @media (max-width: 480px) {
    // Even darker on small mobile devices
    background-color: rgba(0, 0, 0, 0.7);
  }
}

/* ===================
   DRAWER STYLES
   =================== */
.fou-md-drawer {
  background: var(--md-color--white);
  height: 100vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 var(--md-spacing-unit--2xs) var(--md-spacing-unit--lg) rgba(0, 0, 0, 0.15);
  position: relative;

  /* ===================
     POSITIONING & ANIMATIONS
     =================== */
  &--right {
    margin-left: auto;
    animation: slideInFromRight 0.3s ease-out;

    // Faster animation on mobile for better performance
    @media (max-width: 768px) {
      animation: slideInFromRight 0.25s ease-out;
    }
  }

  &--left {
    margin-right: auto;
    animation: slideInFromLeft 0.3s ease-out;

    // Faster animation on mobile for better performance
    @media (max-width: 768px) {
      animation: slideInFromLeft 0.25s ease-out;
    }
  }

  // Disable transitions when transition="none"
  &--no-transition {
    &.fou-md-drawer--right {
      animation: none !important;
    }
    &.fou-md-drawer--left {
      animation: none !important;
    }
  }

  /* ===================
     CLOSE ICON STYLES
     =================== */
  &__close-icon {
    position: absolute;
    top: var(--md-spacing-unit--md);
    right: var(--md-spacing-unit--md);
    z-index: 10;
    cursor: pointer;
    color: var(--md-color--spirits--600);
    padding: var(--md-spacing-unit--xs);
    transition: all 0.2s ease;

    // Touch-friendly sizing
    min-width: 44px;
    min-height: 44px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: var(--md-border-radius--half);

    &:hover {
      background-color: var(--md-color--spirits--200);
      color: var(--md-color--spirits--800);
    }

    // Touch feedback for mobile
    &:active {
      background-color: var(--md-color--spirits--300);
      transform: scale(0.95);
    }
  }

  /* ===================
     HEADER STYLES
     =================== */
  &__header {
    flex: 0 0 auto; // Don't grow or shrink
    padding: var(--md-spacing-unit--2xl) var(--md-spacing-unit--2xl) var(--md-spacing-unit--lg)
      var(--md-spacing-unit--2xl); // Extra right padding for close icon
    border-bottom: 1px solid var(--md-color--spirits--300);
    background: var(--md-color--white);

    &--no-border {
      border-bottom: none;
    }
  }

  /* ===================
     BODY STYLES (SCROLLABLE)
     =================== */
  &__body {
    flex: 1; // Take remaining space
    padding: var(--body-vertical-padding) var(--body-horizontal-padding);
    overflow-y: auto;
    overflow-x: hidden;
    background: var(--md-color--white);

    // Ensure proper scrolling in flex container
    min-height: 0;
  }

  /* ===================
     FOOTER STYLES
     =================== */
  &__footer {
    flex: 0 0 auto; // Don't grow or shrink
    padding: var(--md-spacing-unit--lg) 0px var(--md-spacing-unit--2xl) var(--md-spacing-unit--2xl);
    border-top: 1px solid var(--md-color--spirits--300);
    background: var(--md-color--white);

    &--no-border {
      border-top: none;
    }
  }

  /* ===================
     RESPONSIVE BREAKPOINTS
     =================== */

  // Large tablets and small desktops (769px - 1024px)
  @media (min-width: 769px) and (max-width: 1024px) {
    // Ensure drawer doesn't exceed 85% on tablets
    max-width: 85vw !important;

    &__header {
      padding: var(--md-spacing-unit--xl) var(--md-spacing-unit--3xl) var(--md-spacing-unit--sm)
        var(--md-spacing-unit--xl);
    }

    &__body {
      padding: var(--md-spacing-unit--sm) var(--md-spacing-unit--xl);
    }

    &__footer {
      padding: var(--md-spacing-unit--sm) var(--md-spacing-unit--xl) var(--md-spacing-unit--xl)
        var(--md-spacing-unit--xl);
    }

    &__close-icon {
      top: var(--md-spacing-unit--sm);
      right: var(--md-spacing-unit--sm);
    }
  }

  // Tablets and small screens (481px - 768px)
  @media (min-width: 481px) and (max-width: 768px) {
    // Take more space on tablets but not full width
    max-width: 90vw !important;
    min-width: 320px;

    &__header {
      padding: var(--md-spacing-unit--lg) var(--md-spacing-unit--3xl) var(--md-spacing-unit--sm)
        var(--md-spacing-unit--lg);
    }

    &__body {
      padding: var(--md-spacing-unit--sm) var(--md-spacing-unit--lg);
    }

    &__footer {
      padding: var(--md-spacing-unit--sm) var(--md-spacing-unit--lg) var(--md-spacing-unit--lg)
        var(--md-spacing-unit--lg);
    }

    &__close-icon {
      top: var(--md-spacing-unit--sm);
      right: var(--md-spacing-unit--sm);
      padding: 10px;
    }
  }

  // Mobile devices (320px - 480px)
  @media (max-width: 480px) {
    width: 100vw !important;
    max-width: 100vw !important;
    min-height: 100vh;

    &__header {
      padding: var(--md-spacing-unit--md) var(--md-spacing-unit--2xl) var(--md-spacing-unit--sm)
        var(--md-spacing-unit--md);
      font-size: 0.95rem;
    }

    &__body {
      padding: var(--md-spacing-unit--sm) var(--md-spacing-unit--md);
      font-size: 0.9rem;
    }

    &__footer {
      padding: var(--md-spacing-unit--sm) var(--md-spacing-unit--md) var(--md-spacing-unit--md)
        var(--md-spacing-unit--md);
    }

    &__close-icon {
      top: 10px;
      right: 10px;
      padding: var(--md-spacing-unit--xs);
      width: var(--md-spacing-unit--xl);
      height: var(--md-spacing-unit--xl);
    }
  }

  // Very small mobile devices (max 320px)
  @media (max-width: 320px) {
    &__header {
      padding: var(--md-spacing-unit--sm) 36px 10px var(--md-spacing-unit--sm);
    }

    &__body {
      padding: 10px var(--md-spacing-unit--sm);
    }

    &__footer {
      padding: 10px var(--md-spacing-unit--sm) var(--md-spacing-unit--sm) var(--md-spacing-unit--sm);
    }

    &__close-icon {
      top: var(--md-spacing-unit--xs);
      right: var(--md-spacing-unit--xs);
      width: 28px;
      height: 28px;
    }
  }

  // Large screens (1025px and above)
  @media (min-width: 1025px) {
    // Ensure minimum and maximum widths on large screens
    min-width: 300px;
    max-width: 80vw;
  }
}

/* ===================
   ANIMATIONS
   =================== */
@keyframes slideInFromRight {
  from {
    transform: translateX(100%);
  }
  to {
    transform: translateX(0);
  }
}

@keyframes slideInFromLeft {
  from {
    transform: translateX(-100%);
  }
  to {
    transform: translateX(0);
  }
}

/* ===================
   TRANSITIONS & BORDERED STYLES
   =================== */
.fou-md-drawer--bordered {
  border: 1px solid var(--md-color--spirits--400);
  border-radius: var(--md-border-radius--md);

  // Adjust border on smaller screens
  @media (max-width: 768px) {
    border-radius: var(--md-border-radius--sm);
  }

  @media (max-width: 480px) {
    border: none; // Remove border on mobile for full-screen feel
    border-radius: 0;
  }
}

/* ===================
   SCROLLBAR STYLING
   =================== */
.fou-md-drawer__body {
  // Custom scrollbar for webkit browsers
  &::-webkit-scrollbar {
    width: var(--md-spacing-unit--2xs);
  }

  &::-webkit-scrollbar-track {
    background: var(--md-color--spirits--100);
    border-radius: var(--md-border-radius--xs);
  }

  &::-webkit-scrollbar-thumb {
    background: var(--md-color--spirits--400);
    border-radius: var(--md-border-radius--xs);

    &:hover {
      background: var(--md-color--spirits--500);
    }
  }

  // For Firefox
  scrollbar-width: thin;
  scrollbar-color: var(--md-color--spirits--400) var(--md-color--spirits--100);
}

/* ===================
   FOCUS & ACCESSIBILITY
   =================== */
.fou-md-drawer__close-icon:focus-visible {
  outline: 2px solid var(--md-color--blue-duck);
  outline-offset: var(--md-spacing-unit--3xs);
  border-radius: var(--md-border-radius--sm);
}

/* ===================
   ADDITIONAL RESPONSIVE IMPROVEMENTS
   =================== */
@media (max-width: 768px) {
  .fou-md-drawer {
    // Improve performance on mobile by using transform3d
    transform: translate3d(0, 0, 0);

    // Better touch scrolling
    &__body {
      -webkit-overflow-scrolling: touch;
    }
  }
}

// High DPI displays
@media (-webkit-min-device-pixel-ratio: 2), (min-resolution: 192dpi) {
  .fou-md-drawer {
    // Sharper borders on high DPI displays
    &__header {
      border-bottom-width: 0.5px;
    }

    &__footer {
      border-top-width: 0.5px;
    }
  }
}
</style>
